import {DatePipe} from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from "@angular/core";
import {TranslateModule} from "@ngx-translate/core";
import {
  DiscussionEntry,
  DiscussionEntryKind,
  DiscussionParticipant,
} from "../../interfaces/discussion.interface";
import {DiscussionTextComponent} from "../discussion-text/discussion-text.component";
import {UserAvatarComponent} from "src/app/modules/shared/components/user-avatar/user-avatar.component";
import {WorksTypes} from "src/app/enums/Works.enum";
import {getWorkRatingTone} from "src/app/modules/shared/utils/work-rating.util";
import {DiscussionReactionsComponent} from "../discussion-reactions/discussion-reactions.component";
import {DiscussionGestureDirective} from "../../directives/discussion-gesture.directive";
import {DiscussionCopyComponent} from "../discussion-copy/discussion-copy.component";
import type {PublicWorkResponse} from "src/app/modules/works/interfaces/public-work.interface";

@Component({
  selector: "app-discussion-message",
  templateUrl: "./discussion-message.component.html",
  imports: [
    DatePipe,
    TranslateModule,
    DiscussionTextComponent,
    UserAvatarComponent,
    DiscussionReactionsComponent,
    DiscussionGestureDirective,
    DiscussionCopyComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiscussionMessageComponent {
  @Input({required: true}) entry!: DiscussionEntry;
  @Input() author?: DiscussionParticipant;
  @Input() own = false;
  @Input() authorOnline = false;
  @Input() seen = false;
  @Input() deleting = false;
  @Input() canReact = false;
  @Input() participants: DiscussionParticipant[] = [];
  @Input() userId = "";
  @Input() workType?: WorksTypes;
  @Output() remove = new EventEmitter<string>();
  @Output() report = new EventEmitter<string>();
  @Output() openWork = new EventEmitter<PublicWorkResponse>();
  revealed = false;
  confirming = false;
  removeMessage(reactions: DiscussionReactionsComponent): void {
    if (!this.confirming) {
      this.confirming = true;
      return;
    }
    reactions.close();
    this.remove.emit(this.entry._id);
  }

  get reactionEnabled(): boolean {
    return (
      this.canReact &&
      !this.deleting &&
      !this.entry.deletedAt &&
      !this.entry.moderatedAt &&
      (!this.entry.spoiler || this.revealed)
    );
  }
  readonly activityIcons: Record<DiscussionEntryKind, string> = {
    message: "chat",
    followed: "bookmark",
    listed: "add-to-list",
    started: "play",
    completed: "check",
    rated: "star",
  };

  get activityClasses(): string {
    if (this.entry.kind !== "rated" || this.entry.rating === undefined) {
      return "border-borders-light dark:border-borders-darker text-grey dark:text-lightGrey";
    }
    const color = {
      red: "text-form-error dark:text-form-errorLight",
      orange: "text-primary-text dark:text-primary",
      yellow: "text-yellow-rating-dark",
      green: "text-form-success dark:text-form-successLight",
    }[getWorkRatingTone(this.entry.rating)];
    return `border-current ${color}`;
  }

  activityKey(): string {
    let action: string = this.entry.kind;
    if (action === "completed") {
      if (
        this.workType === WorksTypes.Movie ||
        this.workType === WorksTypes.Series
      )
        action = "seen";
      if (
        [WorksTypes.Book, WorksTypes.Comic, WorksTypes.Manga].some(
          (type) => type === this.workType
        )
      )
        action = "read";
    }
    return `DISCUSSION.EVENT_${this.own ? "SELF_" : ""}${action}`;
  }
}
